import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banknote,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  FileText,
  RefreshCw,
  Search,
  TrendingUp,
  UserRound,
  X,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import api from '../../services/api';
import AdminPageHeader from './AdminPageHeader';
import AdminTableSkeleton from './AdminTableSkeleton';
import Modal from '../common/design-system/Modal';
import './AdminStyles.scss';
import './PaymentManagement.scss';

const PAGE_SIZE = 25;

const getRows = payload => (Array.isArray(payload) ? payload : payload?.results || []);

const getPageMeta = payload => ({
  count: payload?.count ?? getRows(payload).length,
  next: Boolean(payload?.next),
  previous: Boolean(payload?.previous),
  pageSize: payload?.page_size || PAGE_SIZE,
  pages: payload?.pages,
});

const getUserLabel = record => (
  record?.user_username
  || record?.user_email
  || record?.payment?.user_username
  || record?.subscription?.user_username
  || (typeof record?.user === 'object' ? record.user.username || record.user.email : record?.user)
  || 'Sin usuario'
);

const getPaymentReference = record => (
  record?.stripe_payment_intent_id
  || record?.stripe_invoice_id
  || record?.stripe_checkout_session_id
  || record?.stripe_charge_id
  || record?.metadata?.stripe_payment_intent_id
  || record?.metadata?.stripe_invoice_id
  || record?.metadata?.stripe_checkout_session_id
  || record?.metadata?.stripe_charge_id
  || record?.payment?.stripe_invoice_id
  || record?.payment?.stripe_checkout_session_id
  || record?.paid_collab?.stripe_payment_intent_id
  || record?.paid_collab?.stripe_checkout_session_id
  || record?.details?.stripe_payment_intent_id
  || record?.details?.stripe_invoice_id
  || record?.details?.stripe_checkout_session_id
  || record?.details?.stripe_charge_id
  || 'Sin referencia'
);

const getStatus = record => (
  record?.status
  || record?.payment_status
  || record?.details?.payment_status
  || record?.details?.stripe_status
  || 'unknown'
);

const statusLabel = status => ({
  completed: 'Completado',
  paid: 'Pagado',
  processing: 'Procesando',
  pending: 'Pendiente',
  checkout_pending: 'Checkout pendiente',
  requires_action: 'Requiere acción',
  failed: 'Fallido',
  payment_failed: 'Pago fallido',
  refunded: 'Reembolsado',
  partially_refunded: 'Reembolso parcial',
  fully_refunded: 'Reembolso completo',
  disputed: 'En disputa',
  past_due: 'Vencido',
  active: 'Activa',
  canceled: 'Cancelada',
  cancelled: 'Cancelada',
  expired: 'Expirada',
  unknown: 'Sin estado',
}[status] || status.replaceAll('_', ' '));

const statusTone = status => {
  if (['completed', 'paid', 'active'].includes(status)) return 'success';
  if (['processing', 'pending', 'checkout_pending', 'requires_action', 'past_due'].includes(status)) return 'warning';
  if (['failed', 'payment_failed', 'disputed'].includes(status)) return 'danger';
  if (['refunded', 'partially_refunded', 'fully_refunded'].includes(status)) return 'gold';
  return 'neutral';
};

const formatDate = value => {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-PA', { dateStyle: 'medium', timeStyle: 'short' });
};

const formatAmount = (amount, currency = 'USD') => new Intl.NumberFormat('es-PA', {
  style: 'currency', currency,
}).format(Number(amount || 0));

const paymentAmount = record => record?.amount ?? record?.paid_collab?.gross_amount ?? 0;

const transactionConcept = record => (
  record?.paid_collab?.collab_title
    ? record.paid_collab.collab_title
    : record?.subscription?.plan?.name
      || record?.payment?.plan?.name
      || record?.details?.plan_name
      || record?.details?.transaction_scope
      || 'Movimiento financiero'
);

const transactionTypeLabel = type => ({
  payment: 'Pago',
  refund: 'Reembolso',
  subscription_created: 'Suscripción creada',
  subscription_updated: 'Suscripción actualizada',
  subscription_canceled: 'Suscripción cancelada',
  offer_activated: 'Oferta activada',
  paid_collab_payment: 'Paid Collab',
  paid_collab_refund: 'Reembolso Paid Collab',
  paid_collab_position_adjustment: 'Ajuste Paid Collab',
}[type] || type?.replaceAll('_', ' ') || 'Movimiento');

const Pagination = ({ page, meta, onChange, label }) => {
  const pageSize = meta.pageSize || PAGE_SIZE;
  const totalPages = meta.pages || Math.max(1, Math.ceil(meta.count / pageSize));
  return (
    <div className="payment-console__pagination" aria-label={`Paginación de ${label}`}>
      <span>{meta.count === 0 ? 'Sin registros' : `Mostrando ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, meta.count)} de ${meta.count}`}</span>
      <div className="payment-console__pagination-controls">
        <button type="button" className="payment-console__page-button" disabled={!meta.previous} onClick={() => onChange(page - 1)} aria-label="Página anterior"><ChevronLeft size={16} /></button>
        <span aria-live="polite">Página {page} de {totalPages}</span>
        <button type="button" className="payment-console__page-button" disabled={!meta.next} onClick={() => onChange(page + 1)} aria-label="Página siguiente"><ChevronRight size={16} /></button>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => (
  <span className={`payment-console__status payment-console__status--${statusTone(status)}`}>
    <span className="payment-console__status-dot" aria-hidden="true" />
    {statusLabel(status)}
  </span>
);

const PaymentManagement = () => {
  const { isAuthenticated, currentUser } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({ totalRevenue: 0, monthlyRevenue: 0, activeSubscriptions: 0, totalTransactions: 0, currency: 'USD', breakdown: {} });
  const [pages, setPages] = useState({ payments: 1, transactions: 1, subscriptions: 1 });
  const [meta, setMeta] = useState({ payments: { count: 0, next: false, previous: false }, transactions: { count: 0, next: false, previous: false }, subscriptions: { count: 0, next: false, previous: false } });
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: 'all', search: '', environment: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState('payments');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser || (!currentUser.is_staff && !currentUser.is_superuser)) navigate('/dashboard');
  }, [currentUser, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    const fetchData = async () => {
      setRefreshing(true);
      setError('');
      const sharedParams = { start_date: filters.startDate || undefined, end_date: filters.endDate || undefined, search: debouncedSearch || undefined, page_size: PAGE_SIZE, environment: filters.environment || undefined };
      try {
        const [listResponse, statsResponse] = await Promise.all([
          api.get(`/admin/${activeTab}/`, { params: { ...sharedParams, page: pages[activeTab], status: filters.status === 'all' ? undefined : filters.status } }),
          api.get('/admin/payment-stats/', { params: sharedParams }),
        ]);
        if (cancelled) return;
        ({ payments: setPayments, transactions: setTransactions, subscriptions: setSubscriptions })[activeTab](getRows(listResponse.data));
        setMeta(previous => ({ ...previous, [activeTab]: getPageMeta(listResponse.data) }));
        setStats({ totalRevenue: statsResponse.data.total_revenue || 0, monthlyRevenue: statsResponse.data.monthly_revenue || 0, activeSubscriptions: statsResponse.data.active_subscriptions || 0, totalTransactions: statsResponse.data.total_transactions || 0, currency: statsResponse.data.currency || 'USD', breakdown: statsResponse.data.financial_breakdown?.lifetime || {}, scope: statsResponse.data.scope, unclassified: statsResponse.data.unclassified_records });
      } catch {
        if (!cancelled) setError('No se pudo cargar el libro de transacciones. Intenta nuevamente.');
      } finally {
        if (!cancelled) { setLoading(false); setRefreshing(false); }
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [activeTab, debouncedSearch, filters.endDate, filters.startDate, filters.status, filters.environment, isAuthenticated, pages]);

  useEffect(() => {
    setPages({ payments: 1, transactions: 1, subscriptions: 1 });
  }, [debouncedSearch, filters.endDate, filters.startDate, filters.status, filters.environment]);

  const handleFilterChange = event => setFilters(previous => ({ ...previous, [event.target.name]: event.target.value }));
  const resetFilters = () => { setFilters({ startDate: '', endDate: '', status: 'all', search: '', environment: '' }); setPages({ payments: 1, transactions: 1, subscriptions: 1 }); };

  const detailRows = useMemo(() => {
    if (!selectedRecord) return [];
    return [
      ['Usuario', getUserLabel(selectedRecord)],
      ['Concepto', transactionConcept(selectedRecord)],
      ['Monto', formatAmount(paymentAmount(selectedRecord), selectedRecord.currency || selectedRecord.paid_collab?.currency || 'USD')],
      ['Estado', statusLabel(getStatus(selectedRecord))],
      ['Entorno', selectedRecord.environment || selectedRecord.paid_collab?.environment || 'Sin clasificar'],
      ['Referencia Stripe', getPaymentReference(selectedRecord)],
      ['Fecha', formatDate(selectedRecord.created_at)],
      ['Actualizado', formatDate(selectedRecord.updated_at)],
    ];
  }, [selectedRecord]);

  if (loading) return <div className="payment-console"><AdminPageHeader title="Gestión de Pagos" subtitle="Libro financiero de pagos, reembolsos y suscripciones." breadcrumbs="Transacciones" /><AdminTableSkeleton columns={6} rows={7} /></div>;

  return (
    <div className="payment-console">
      <AdminPageHeader title="Gestión de Pagos" subtitle="Un registro único y trazable de cada movimiento financiero de Foth." breadcrumbs="Transacciones" />

      <div className="payment-console__toolbar">
        <div><span className="payment-console__eyebrow"><FileText size={14} /> Libro financiero</span><p>Los registros se sincronizan desde Stripe y las operaciones internas.</p></div>
        <button type="button" className="payment-console__refresh" onClick={() => setPages(previous => ({ ...previous }))} disabled={refreshing}><RefreshCw size={16} className={refreshing ? 'is-spinning' : ''} />{refreshing ? 'Actualizando' : 'Actualizar'}</button>
      </div>
      {error && <div className="payment-console__alert" role="alert"><CircleAlert size={18} /> {error}</div>}

      <section className="payment-console__summary" aria-label="Resumen financiero">
        <article className="payment-console__metric payment-console__metric--featured"><span>{filters.startDate || filters.endDate ? 'Ingresos brutos FOTH del período' : 'Ingresos brutos FOTH acumulados'}</span><strong>{formatAmount(stats.totalRevenue, stats.currency)}</strong><Banknote size={22} aria-hidden="true" /></article>
        <article className="payment-console__metric"><span>Ingresos brutos FOTH del mes</span><strong>{formatAmount(stats.monthlyRevenue, stats.currency)}</strong><TrendingUp size={22} aria-hidden="true" /></article>
        <article className="payment-console__metric"><span>Suscripciones activas</span><strong>{stats.activeSubscriptions}</strong><UserRound size={22} aria-hidden="true" /></article>
        <article className="payment-console__metric"><span>Movimientos registrados</span><strong>{stats.totalTransactions}</strong><FileText size={22} aria-hidden="true" /></article>
      </section>

      <section className="payment-console__financial-breakdown" aria-label="Desglose financiero acumulado">
        <h2>Desglose financiero del período seleccionado</h2>
        <p>Entorno: {stats.scope?.environment || 'configurado'}. Las tarjetas siguen las fechas y la búsqueda; el filtro de estado aplica a la tabla seleccionada.</p>
        {(stats.unclassified?.payments > 0 || stats.unclassified?.subscriptions > 0) && <p>Hay registros históricos sin entorno: {stats.unclassified.payments} pagos y {stats.unclassified.subscriptions} suscripciones. Puedes consultarlos seleccionando «Sin clasificar».</p>}
        <p>Los ingresos brutos FOTH incluyen suscripciones y fees de servicio, antes de devoluciones y costos. La compensación de creadores, los cargos de procesamiento y las reservas para payouts se muestran por separado.</p>
        {Object.entries(stats.breakdown).map(([currency, amounts]) => (
          <div key={currency}>
            <h3>{currency}</h3>
            <dl>
              {[
                ['Suscripciones cobradas', amounts.subscription_charges],
                ['Fees FOTH cobrados por collabs', amounts.collab_foth_fees],
                ['Compensación de creadores incluida en los cobros', amounts.creator_compensation],
                ['Cargos de procesamiento cobrados', amounts.processing_charges],
                ['Reservas para payouts cobradas', amounts.payout_reserves],
                ['Volumen total cobrado', amounts.gross_collected],
                ['Devoluciones totales', amounts.refunds],
                ['Cobros después de devoluciones', amounts.net_collected],
                ['Costos Stripe confirmados de collabs', amounts.known_collab_stripe_costs],
              ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{formatAmount(value, currency)}</dd></div>)}
            </dl>
            {amounts.stripe_costs_missing_count > 0 && <p>Faltan costos Stripe confirmados para {amounts.stripe_costs_missing_count} cobros de collabs.</p>}
          </div>
        ))}
        <p>Las devoluciones totales pueden incluir dinero de creadores y reservas. Este desglose no calcula utilidad ni ingresos netos FOTH.</p>
      </section>

      <nav className="payment-console__tabs" aria-label="Vistas financieras" role="tablist">
        {[['payments', 'Pagos'], ['transactions', 'Libro de transacciones'], ['subscriptions', 'Suscripciones']].map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={activeTab === key} className={activeTab === key ? 'is-active' : ''} onClick={() => { setFilters(previous => ({ ...previous, status: 'all' })); setActiveTab(key); }}>{label}</button>)}
      </nav>

      <section className="payment-console__filters" aria-label="Filtros del libro financiero">
        <label><span>Desde</span><div className="payment-console__input-wrap"><CalendarDays size={16} /><input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} /></div></label>
        <label><span>Hasta</span><div className="payment-console__input-wrap"><CalendarDays size={16} /><input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} /></div></label>
        <label><span>Estado de la tabla</span><select name="status" value={filters.status} onChange={handleFilterChange}><option value="all">Todos los estados</option>{(activeTab === 'subscriptions' ? ['active', 'trialing', 'past_due', 'canceled', 'expired'] : activeTab === 'transactions' ? ['completed', 'paid', 'pending', 'failed', 'refunded', 'partially_refunded', 'fully_refunded'] : ['completed', 'pending', 'failed', 'refunded']).map(status => <option key={status} value={status}>{statusLabel(status)}</option>)}</select></label>
        <label><span>Entorno</span><select name="environment" value={filters.environment} onChange={handleFilterChange}><option value="">Entorno configurado</option><option value="live">Live</option><option value="test">Test</option><option value="unknown">Sin clasificar</option><option value="all">Todos (mezclados)</option></select></label>
        <label className="payment-console__search"><span>Buscar</span><div className="payment-console__input-wrap"><Search size={16} /><input type="search" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Usuario, ID o referencia Stripe" /></div></label>
        <button type="button" className="payment-console__clear" onClick={resetFilters}><X size={15} /> Limpiar</button>
      </section>

      {activeTab === 'payments' && <section className="payment-console__panel" role="tabpanel"><div className="payment-console__panel-header"><div><span className="payment-console__panel-kicker">Cobros y cargos</span><h2>Pagos registrados</h2></div><span>{meta.payments.count} registros</span></div><div className="payment-console__table-scroll"><table className="payment-console__table"><caption className="sr-only">Pagos registrados</caption><thead><tr><th>Fecha</th><th>Usuario</th><th>Plan</th><th>Monto</th><th>Estado</th><th>Referencia</th><th /></tr></thead><tbody>{payments.length ? payments.map(payment => <tr key={payment.id}><td>{formatDate(payment.created_at)}</td><td><strong>{getUserLabel(payment)}</strong><small>{payment.user_email || ''}</small></td><td>{payment.plan?.name || 'Paid Collab'}</td><td className="payment-console__amount">{formatAmount(payment.amount)}</td><td><StatusBadge status={getStatus(payment)} /></td><td className="payment-console__reference">{getPaymentReference(payment)}</td><td><button type="button" className="payment-console__detail" onClick={() => setSelectedRecord(payment)}>Ver detalle</button></td></tr>) : <tr><td colSpan="7" className="payment-console__empty"><CircleCheck size={20} /> No hay pagos con estos filtros.</td></tr>}</tbody></table></div><Pagination page={pages.payments} meta={meta.payments} onChange={page => setPages(previous => ({ ...previous, payments: page }))} label="pagos" /></section>}

      {activeTab === 'transactions' && <section className="payment-console__panel" role="tabpanel"><div className="payment-console__panel-header"><div><span className="payment-console__panel-kicker">Auditoría completa</span><h2>Libro de transacciones</h2></div><span>{meta.transactions.count} movimientos</span></div><div className="payment-console__table-scroll"><table className="payment-console__table"><caption className="sr-only">Libro de transacciones</caption><thead><tr><th>Fecha</th><th>Tipo</th><th>Usuario</th><th>Concepto</th><th>Monto</th><th>Estado</th><th>Referencia</th><th /></tr></thead><tbody>{transactions.length ? transactions.map(transaction => <tr key={transaction.id}><td>{formatDate(transaction.created_at)}</td><td><span className="payment-console__type">{transactionTypeLabel(transaction.transaction_type)}</span></td><td>{getUserLabel(transaction)}</td><td>{transactionConcept(transaction)}</td><td className="payment-console__amount">{formatAmount(paymentAmount(transaction), transaction.paid_collab?.currency || 'USD')}</td><td><StatusBadge status={getStatus(transaction)} /></td><td className="payment-console__reference">{getPaymentReference(transaction)}</td><td><button type="button" className="payment-console__detail" onClick={() => setSelectedRecord(transaction)}>Ver detalle</button></td></tr>) : <tr><td colSpan="8" className="payment-console__empty"><CircleAlert size={20} /> No hay movimientos registrados.</td></tr>}</tbody></table></div><Pagination page={pages.transactions} meta={meta.transactions} onChange={page => setPages(previous => ({ ...previous, transactions: page }))} label="transacciones" /></section>}

      {activeTab === 'subscriptions' && <section className="payment-console__panel" role="tabpanel"><div className="payment-console__panel-header"><div><span className="payment-console__panel-kicker">Ciclo de vida</span><h2>Suscripciones</h2></div><span>{meta.subscriptions.count} registros</span></div><div className="payment-console__table-scroll"><table className="payment-console__table"><caption className="sr-only">Suscripciones</caption><thead><tr><th>Usuario</th><th>Plan</th><th>Estado</th><th>Stripe</th><th>Inicio</th><th>Fin</th></tr></thead><tbody>{subscriptions.length ? subscriptions.map(subscription => <tr key={subscription.id}><td><strong>{getUserLabel(subscription)}</strong><small>{subscription.user_email || ''}</small></td><td>{subscription.plan?.name || '—'}</td><td><StatusBadge status={subscription.status === 'active' && !subscription.is_active ? 'expired' : getStatus(subscription)} /></td><td className="payment-console__reference">{subscription.stripe_subscription_id || 'Sin referencia'}</td><td>{formatDate(subscription.start_date)}</td><td>{formatDate(subscription.end_date)}</td></tr>) : <tr><td colSpan="6" className="payment-console__empty"><CircleAlert size={20} /> No hay suscripciones.</td></tr>}</tbody></table></div><Pagination page={pages.subscriptions} meta={meta.subscriptions} onChange={page => setPages(previous => ({ ...previous, subscriptions: page }))} label="suscripciones" /></section>}

      <Modal open={Boolean(selectedRecord)} onClose={() => setSelectedRecord(null)} title="Detalle del movimiento" footer={<button type="button" className="payment-console__modal-close" onClick={() => setSelectedRecord(null)}>Cerrar</button>}>
        {selectedRecord && <div className="payment-console__detail-grid">{detailRows.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}<div><span>Datos de trazabilidad</span><pre>{JSON.stringify(selectedRecord.details || selectedRecord.metadata || selectedRecord.paid_collab || {}, null, 2)}</pre></div></div>}
      </Modal>
    </div>
  );
};

export default PaymentManagement;
